/**
 * v3.17 分享传播：生成帖子分享海报（canvas 绘制 → PNG 下载/再分享）。
 *
 * 不依赖任何第三方库；中文字体使用浏览器默认字体，深色品牌风。
 */

import type { Post } from '@shared/types';

const W = 1080;
const H = 1520;

/** 自动换行：按宽度折行文本。 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const lines: string[] = [];
  const paragraphs = text.split(/\n+/).filter(Boolean);
  for (const para of paragraphs) {
    let line = '';
    for (const ch of para) {
      const test = line + ch;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = ch;
        if (lines.length >= maxLines) return lines;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    if (lines.length >= maxLines) return lines;
  }
  return lines;
}

/** 生成帖子分享海报，返回可下载/可分享的 object URL。调用方负责 revoke。 */
export async function renderPoster(post: Post): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 不可用');

  // 背景渐变
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#0b1020');
  bg.addColorStop(0.55, '#101a35');
  bg.addColorStop(1, '#0a0e1d');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // 品牌头
  ctx.fillStyle = '#6ee7ff';
  ctx.font = 'bold 52px system-ui, sans-serif';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('MeshLink', 72, 108);
  ctx.fillStyle = '#8b9cc8';
  ctx.font = '28px system-ui, sans-serif';
  ctx.fillText('去中心化社交 · 内容上链 · 行为挖矿', 72, 156);

  // 分隔线
  ctx.strokeStyle = 'rgba(139,156,200,0.35)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(72, 196);
  ctx.lineTo(W - 72, 196);
  ctx.stroke();

  // 作者
  ctx.fillStyle = '#e8eefc';
  ctx.font = 'bold 36px system-ui, sans-serif';
  const authorName = typeof post.authorName === 'string' && post.authorName ? post.authorName : '匿名用户';
  ctx.fillText(authorName.slice(0, 24), 72, 270);
  ctx.fillStyle = '#7181ad';
  ctx.font = '26px system-ui, sans-serif';
  const did = typeof post.authorDid === 'string' && post.authorDid ? post.authorDid : '';
  if (did) {
    const short = did.length > 42 ? `${did.slice(0, 20)}…${did.slice(-12)}` : did;
    ctx.fillText(short, 72, 316);
  }
  const ts = new Date(post.ts).toLocaleString('zh-CN', { hour12: false });
  ctx.fillText(ts, 72, 360);

  // 正文
  const body = typeof post.text === 'string' && post.text ? post.text : '（无正文）';
  ctx.fillStyle = '#f2f6ff';
  ctx.font = '32px system-ui, sans-serif';
  const bodyLines = wrapText(ctx, body, W - 144, 16);
  let y = 450;
  for (const line of bodyLines) {
    ctx.fillText(line, 72, y);
    y += 50;
  }

  // 底部信息
  ctx.strokeStyle = 'rgba(139,156,200,0.35)';
  ctx.beginPath();
  ctx.moveTo(72, H - 220);
  ctx.lineTo(W - 72, H - 220);
  ctx.stroke();
  ctx.fillStyle = '#6ee7ff';
  ctx.font = 'bold 34px system-ui, sans-serif';
  ctx.fillText('内容已上链 · 分享即挖矿', 72, H - 150);
  ctx.fillStyle = '#8b9cc8';
  ctx.font = '26px system-ui, sans-serif';
  ctx.fillText('mesh.doup.cn', 72, H - 96);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob 失败'))), 'image/png');
  });
  return URL.createObjectURL(blob);
}
